
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Bell, User, Calendar, X, MessageSquare, Mail, Phone } from "lucide-react";
import { AdminSettings } from "@/types/adminSettings";

interface GeneralSettingsTabProps {
  settings: AdminSettings;
  updateSettings: (path: string[], value: any) => void;
  saveSettings: () => Promise<void>;
  isSaving: boolean;
}

export const GeneralSettingsTab: React.FC<GeneralSettingsTabProps> = ({
  settings,
  updateSettings,
  saveSettings,
  isSaving
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification Settings</CardTitle>
        <CardDescription>Manage email and SMS notification preferences</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Notification Preferences
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Email Settings Column */}
            <div className="space-y-4 border p-4 rounded-md">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium">Email Notifications</h4>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-email">Admin Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={settings.email.admin}
                  onChange={(e) => updateSettings(['email', 'admin'], e.target.value)}
                  placeholder="Enter admin email address"
                />
                <p className="text-sm text-muted-foreground">
                  The email address where admin notifications will be sent
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="copy-to-admin">Copy Admin on All Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send a copy of all notifications to the admin email
                  </p>
                </div>
                <Switch 
                  id="copy-to-admin"
                  checked={settings.email.copyToAdmin}
                  onCheckedChange={(checked) => updateSettings(['email', 'copyToAdmin'], checked)}
                />
              </div>
            </div>

            {/* SMS Settings Column */}
            <div className="space-y-4 border p-4 rounded-md">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium">SMS Notifications</h4>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="admin-phone">Admin Phone Number</Label>
                <Input
                  id="admin-phone"
                  type="tel"
                  value={settings.sms?.adminPhone || ""}
                  onChange={(e) => updateSettings(['sms', 'adminPhone'], e.target.value)}
                  placeholder="254700230965"
                />
                <p className="text-sm text-muted-foreground">
                  The phone number where SMS notifications will be sent
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="sms-enabled">Enable SMS Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Send notifications via SMS alongside email
                  </p>
                </div>
                <Switch 
                  id="sms-enabled"
                  checked={settings.sms?.enabled || false}
                  onCheckedChange={(checked) => updateSettings(['sms', 'enabled'], checked)}
                />
              </div>
            </div>
          </div>

          {/* Notification Types */}
          <div className="space-y-4 mt-6">
            <h4 className="font-medium">Choose which events to receive notifications for:</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="notify-signups">User Signups</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when new users sign up
                    </p>
                  </div>
                </div>
                <Switch 
                  id="notify-signups"
                  checked={settings.notifications.signups}
                  onCheckedChange={(checked) => updateSettings(['notifications', 'signups'], checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="notify-bookings">New Bookings</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when new bookings are made
                    </p>
                  </div>
                </div>
                <Switch 
                  id="notify-bookings"
                  checked={settings.notifications.bookings}
                  onCheckedChange={(checked) => updateSettings(['notifications', 'bookings'], checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <X className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="notify-cancellations">Cancellations</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when bookings are cancelled
                    </p>
                  </div>
                </div>
                <Switch 
                  id="notify-cancellations"
                  checked={settings.notifications.cancellations}
                  onCheckedChange={(checked) => updateSettings(['notifications', 'cancellations'], checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <Label htmlFor="notify-reviews">New Reviews</Label>
                    <p className="text-sm text-muted-foreground">
                      Get notified when new reviews are submitted
                    </p>
                  </div>
                </div>
                <Switch 
                  id="notify-reviews"
                  checked={settings.notifications.reviews}
                  onCheckedChange={(checked) => updateSettings(['notifications', 'reviews'], checked)}
                />
              </div>
            </div>
          </div>

          <Button 
            onClick={saveSettings} 
            disabled={isSaving}
            className="mt-6"
          >
            {isSaving ? "Saving..." : "Save Notification Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
